import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { Users, GraduationCap, CalendarPlus, CalendarCheck, TrendingDown, Sparkles } from "lucide-react";

export default function DashboardSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setData(data);
      } catch (e) {
        // Silent — non-admin users get 403
      } finally { setLoading(false); }
    })();
  }, []);
  if (loading || !data) return null;
  const avgs = data.avgs || {};
  const wb = data.weiterbildung || {};
  const nextWb = wb.next_class;
  return (
    <div className="space-y-6" data-testid="dashboard-summary">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="AVGS · Aktive Kunden"
          value={avgs.active_count}
          icon={Users}
          color="teal"
          testId="kpi-avgs-active"
          linkTo="/kunden-status"
        />
        <KpiCard
          label="Weiterbildung · Aktive Teilnehmer"
          value={wb.active_participant_count}
          sub={`${wb.active_class_count} laufende Klassen`}
          icon={GraduationCap}
          color="teal"
          testId="kpi-wb-active"
          linkTo="/weiterbildung"
        />
        <KpiCard
          label="Kunden im Endspurt"
          value={(avgs.ending_soon_15d || []).length}
          sub="nächste 15 Tage"
          icon={CalendarCheck}
          color="amber"
          testId="kpi-ending-soon"
          linkTo="/kunden-status"
        />
        <KpiCard
          label="Bald neue Kunden"
          value={(avgs.starting_soon_15d || []).length}
          sub="Kick-off in ≤ 15 Tagen"
          icon={CalendarPlus}
          color="emerald"
          testId="kpi-starting-soon"
          linkTo="/avgs"
        />
      </div>

      {/* Detail rows */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Behind on UEs — motivational red panel */}
        <Panel
          title="UE-Aufholjagd"
          icon={TrendingDown}
          tone={(avgs.behind_on_ue || []).length ? "warn" : "ok"}
          empty={(avgs.behind_on_ue || []).length === 0 ? "Alle Kunden im Plan — top gemacht!" : null}
          testId="panel-behind-ue"
        >
          <div className="divide-y divide-slate-100">
            {(avgs.behind_on_ue || []).slice(0, 6).map((c) => (
              <Link
                key={c.client_id}
                to={`/avgs/${c.client_id}`}
                className="py-2.5 flex items-center justify-between gap-2 hover:bg-slate-50 -mx-2 px-2"
                data-testid={`behind-row-${c.client_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    Genutzt {c.used_ue}/{c.total_ue} UE · bis {formatDeShort(c.end_date)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-heading font-light tracking-tighter text-amber-700">
                    −{c.gap_pct.toFixed(0)}%
                  </div>
                  <div className="text-[9px] uppercase tracking-overline text-slate-400">unter Plan</div>
                </div>
              </Link>
            ))}
            {(avgs.behind_on_ue || []).length > 6 && (
              <Link to="/kunden-status" className="block py-3 text-center text-xs text-slate-500 hover:text-slate-900 font-medium">
                +{avgs.behind_on_ue.length - 6} weitere ansehen →
              </Link>
            )}
          </div>
        </Panel>

        {/* Ending Soon */}
        <Panel
          title="Endspurt · nächste 15 Tage"
          icon={CalendarCheck}
          tone="info"
          empty={(avgs.ending_soon_15d || []).length === 0 ? "Kein Kunde in den nächsten 15 Tagen fertig." : null}
          testId="panel-ending-soon"
        >
          <div className="divide-y divide-slate-100">
            {(avgs.ending_soon_15d || []).map((c) => {
              const daysLeft = Math.max(0, Math.round((new Date(c.end_date) - new Date(data.today)) / 86400000));
              return (
                <Link
                  key={c.client_id}
                  to={`/avgs/${c.client_id}`}
                  className="py-2.5 flex items-center justify-between gap-2 hover:bg-slate-50 -mx-2 px-2"
                  data-testid={`ending-row-${c.client_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">endet {formatDeShort(c.end_date)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-heading font-light tracking-tighter text-slate-900">
                      {daysLeft}<span className="text-xs text-slate-400 ml-1">Tage</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </Panel>

        {/* Starting Soon */}
        <Panel
          title="Frisch dabei · Start in ≤ 15 Tagen"
          icon={CalendarPlus}
          tone="ok"
          empty={(avgs.starting_soon_15d || []).length === 0 ? "Aktuell kein neuer AVGS-Start geplant." : null}
          testId="panel-starting-soon"
        >
          <div className="divide-y divide-slate-100">
            {(avgs.starting_soon_15d || []).map((c) => {
              const days = Math.max(0, Math.round((new Date(c.start_date) - new Date(data.today)) / 86400000));
              return (
                <Link
                  key={c.client_id}
                  to={`/avgs/${c.client_id}`}
                  className="py-2.5 flex items-center justify-between gap-2 hover:bg-slate-50 -mx-2 px-2"
                  data-testid={`starting-row-${c.client_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">Start {formatDeShort(c.start_date)}</div>
                  </div>
                  <div className="text-right shrink-0 text-lg font-heading font-light tracking-tighter text-emerald-700">
                    in {days}<span className="text-xs text-slate-400 ml-1">Tagen</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </Panel>

        {/* Next Weiterbildung */}
        <Panel
          title="Nächste Weiterbildung"
          icon={Sparkles}
          tone={nextWb && nextWb.occupancy_pct < 50 ? "warn" : (nextWb ? "ok" : "muted")}
          empty={!nextWb ? "Aktuell keine kommende Weiterbildungs-Klasse geplant." : null}
          testId="panel-next-wb"
        >
          {nextWb && (
            <Link to={`/weiterbildung/klasse/${nextWb.class_id}`} className="block -mx-2 px-2 py-2 hover:bg-slate-50" data-testid="next-wb-card">
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">
                {nextWb.measure_number} · {nextWb.weiterbildung_name}
              </div>
              <div className="font-heading text-xl font-light tracking-tighter text-slate-900 mb-2">{nextWb.name}</div>
              <div className="text-xs text-slate-600 font-mono mb-3">
                Start {formatDeShort(nextWb.start_date)} · Ende {formatDeShort(nextWb.end_date)}
              </div>
              <div className="mb-1 flex items-baseline justify-between text-[10px] uppercase tracking-overline">
                <span className="text-slate-500">Auslastung ({nextWb.participant_count}/{nextWb.capacity} Kunden)</span>
                <span className={nextWb.occupancy_pct >= 100 ? "text-red-700" : nextWb.occupancy_pct >= 80 ? "text-emerald-700" : nextWb.occupancy_pct < 50 ? "text-amber-700" : "text-slate-700"}>
                  {nextWb.occupancy_pct}%
                </span>
              </div>
              <div className="h-3 bg-slate-100 overflow-hidden">
                <div
                  className={nextWb.occupancy_pct >= 100 ? "h-full bg-red-500" : nextWb.occupancy_pct >= 80 ? "h-full bg-emerald-500" : nextWb.occupancy_pct >= 50 ? "h-full bg-slate-500" : "h-full bg-amber-500"}
                  style={{ width: `${Math.max(4, Math.min(100, nextWb.occupancy_pct))}%` }}
                />
              </div>
              <div className="text-[10px] text-slate-500 mt-2 italic">
                100 % = 12 Kunden pro Klasse
              </div>
              {nextWb.occupancy_pct < 50 && (
                <div className="mt-3 border-l-4 border-amber-500 bg-amber-50 p-3" data-testid="next-wb-warn-low">
                  <div className="text-xs font-bold text-amber-900 mb-0.5">Achtung: Auslastung unter 50 %</div>
                  <div className="text-[11px] text-amber-800">
                    Aktuell nur {nextWb.participant_count} von {nextWb.capacity} Plätzen belegt. Jetzt Kunden aktivieren, damit der Kurs voll wird.
                  </div>
                </div>
              )}
            </Link>
          )}
        </Panel>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, color, testId, linkTo }) {
  const colorMap = {
    teal: "border-l-[#0a788c]",
    amber: "border-l-amber-500",
    emerald: "border-l-emerald-500",
    red: "border-l-red-500",
    slate: "border-l-slate-400",
  };
  const inner = (
    <div className={`border border-slate-200 border-l-4 ${colorMap[color] || colorMap.slate} p-4 bg-white hover:bg-slate-50 transition-colors`} data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">{label}</div>
          <div className="font-heading text-3xl font-light tracking-tighter text-slate-900">{value ?? "—"}</div>
          {sub && <div className="text-[10px] text-slate-500 font-mono mt-1">{sub}</div>}
        </div>
        <Icon size={18} strokeWidth={1.5} className="text-slate-400 shrink-0 mt-1" />
      </div>
    </div>
  );
  return linkTo ? <Link to={linkTo}>{inner}</Link> : inner;
}

function Panel({ title, icon: Icon, tone = "info", empty, children, testId }) {
  const toneMap = {
    ok: "border-emerald-200",
    warn: "border-amber-200",
    info: "border-slate-200",
    muted: "border-slate-200 opacity-70",
  };
  return (
    <div className={`border ${toneMap[tone]} bg-white p-5`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={14} strokeWidth={1.5} className="text-slate-500" />
        <div className="text-xs font-bold uppercase tracking-overline text-slate-700">{title}</div>
      </div>
      {empty ? <div className="text-xs text-slate-400 italic py-3">{empty}</div> : children}
    </div>
  );
}
